import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import type { EditorPluginAIProvider } from '../../../types/types';
import { CONFIG_ITEM_KEYS } from '../../../ui/prebuilt/config-items/config-item-keys';
import { getAllEditorPluginAIConfigItems } from '../../../ui/prebuilt/config-items/config-items';
import type { EditorPluginAIConfigItem } from '../../../ui/prebuilt/config-items/config-items';
import { suggestTitleForConfluenceTitleToolbar } from '../../../ui/prebuilt/config-items/prompts/suggest-title/suggest-title';
import { EventHubPrompt } from '../types';
import type {
	AdjustSelectionInstruction,
	ConfigType,
	StartPromptData,
	AdditionalPayloadDataForIssueReformatter,
	DescriptionCompleterAdditionalPayload,
	AdditionalPayloadDataForDraftReplyPill,
	IncidentSmartCommentsAdditionalPayload,
} from '../types';

const getConfigFromProvider = (
	editorPluginAIProvider: EditorPluginAIProvider,
	configType: ConfigType,
	key: CONFIG_ITEM_KEYS,
) => {
	if (key === CONFIG_ITEM_KEYS.FREE_GENERATE) {
		return editorPluginAIProvider.baseGenerate;
	} else {
		const configItem = getAllEditorPluginAIConfigItems({
			configItemWithOptions: editorPluginAIProvider.configItemWithOptions,
		}).find((suggestion) => suggestion.key === key);
		return configItem;
	}
};

const getConfigTypeByAdjustSelection = (
	adjustSelection?: AdjustSelectionInstruction,
): ConfigType => {
	switch (adjustSelection) {
		case 'fullDocument': {
			return 'rangeConfig';
		}
		case 'keepCurrent': {
			return 'rangeConfig';
		}
		default: {
			return 'emptyConfig';
		}
	}
};

export const getConfigItem = (
	prompt: StartPromptData['prompt'],
	editorPluginAIProvider: EditorPluginAIProvider,
	adjustSelection?: AdjustSelectionInstruction,
	additionalPayloadData?:
		| AdditionalPayloadDataForIssueReformatter
		| DescriptionCompleterAdditionalPayload
		| AdditionalPayloadDataForDraftReplyPill
		| IncidentSmartCommentsAdditionalPayload,
): EditorPluginAIConfigItem | undefined => {
	switch (prompt) {
		case EventHubPrompt.SuggestTitleConfluenceTitleToolbar: {
			return suggestTitleForConfluenceTitleToolbar();
		}

		case EventHubPrompt.SuggestTitle: {
			const suggestTitle = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.SUGGEST_A_TITLE,
			);
			return suggestTitle;
		}

		case EventHubPrompt.DraftReply: {
			const draftReply = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.SUGGEST_RESPONSE,
			);
			return draftReply;
		}

		case EventHubPrompt.GenerateEscalationNotes: {
			const generateEscalationNotes = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.GENERATE_ESCALATION_NOTES,
			);
			return generateEscalationNotes;
		}

		case EventHubPrompt.IncidentSmartComments: {
			const incidentSmartComments = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.INCIDENT_SMART_COMMENTS,
			);
			return getIncidentSmartCommentsConfigItem(
				incidentSmartComments,
				additionalPayloadData as IncidentSmartCommentsAdditionalPayload,
			);
		}

		case EventHubPrompt.CustomPrompt: {
			const freeGenerate = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.FREE_GENERATE,
			);
			return freeGenerate;
		}

		case EventHubPrompt.IssueReformatter: {
			const issueReformatter = getConfigFromProvider(
				editorPluginAIProvider,
				'rangeConfig',
				CONFIG_ITEM_KEYS.ADD_STRUCTURE,
			);
			return getIssueReformatterModifiedConfigItem(
				issueReformatter,
				additionalPayloadData as AdditionalPayloadDataForIssueReformatter,
			);
		}

		case EventHubPrompt.DescriptionCompleter: {
			const descriptionCompleter = getConfigFromProvider(
				editorPluginAIProvider,
				'rangeConfig',
				CONFIG_ITEM_KEYS.ADD_STRUCTURE,
			);
			return getDescriptionCompleterConfigItem(
				descriptionCompleter,
				additionalPayloadData as DescriptionCompleterAdditionalPayload,
			);
		}

		case EventHubPrompt.DraftReplyPill: {
			const draftReplyPill = getConfigFromProvider(
				editorPluginAIProvider,
				'rangeConfig',
				CONFIG_ITEM_KEYS.SUGGEST_RESPONSE,
			);
			return getDraftReplyPillConfigItem(
				draftReplyPill,
				additionalPayloadData as AdditionalPayloadDataForDraftReplyPill,
			);
		}

		case EventHubPrompt.Summarize: {
			const summarize = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.SUMMARISE_WRITING,
			);
			return summarize;
		}

		case EventHubPrompt.ImproveWriting: {
			const improveWriting = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.IMPROVE_WRITING,
			);
			return improveWriting;
		}

		case EventHubPrompt.MakeShorter: {
			const makeShorter = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.MAKE_SHORTER,
			);
			return makeShorter;
		}

		case EventHubPrompt.FixSpellingGrammar: {
			const fixSpellingGrammar = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.FIX_SPELLING_AND_GRAMMAR,
			);
			return fixSpellingGrammar;
		}

		case EventHubPrompt.ChangeToneToProfessional: {
			const professionalTone = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
			);
			return professionalTone;
		}

		case EventHubPrompt.ChangeToneToNeutral: {
			const neutralTone = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
			);
			return neutralTone;
		}

		case EventHubPrompt.ChangeToneToEmpathetic: {
			const empatheticTone = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
			);
			return empatheticTone;
		}

		case EventHubPrompt.ChangeToneToCasual: {
			const empatheticTone = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
			);
			return empatheticTone;
		}

		case EventHubPrompt.ChangeToneToEducational: {
			const empatheticTone = getConfigFromProvider(
				editorPluginAIProvider,
				getConfigTypeByAdjustSelection(adjustSelection),
				CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
			);
			return empatheticTone;
		}

		default: {
			return undefined;
		}
	}
};

export const shouldReturnFallbackSuggestedTitle = (
	configItem: ReturnType<typeof getConfigItem>,
	currentEditorView: EditorView,
): boolean => {
	return (
		configItem?.key === CONFIG_ITEM_KEYS.SUGGEST_A_TITLE &&
		currentEditorView.state.doc.textContent.trim() === ''
	);
};

export const getIssueReformatterModifiedConfigItem = (
	configItem?: EditorPluginAIConfigItem,
	additionalPayload?: AdditionalPayloadDataForIssueReformatter,
): EditorPluginAIConfigItem | undefined => {
	if (
		configItem?.getRequestPayload &&
		additionalPayload?.feature === 'proactive-improve-description'
	) {
		const modifiedConfigItem: EditorPluginAIConfigItem = {
			...configItem,
			experienceId: 'jira-work-completer',
			getRequestPayload: (...params) => {
				const requestPayload = configItem.getRequestPayload?.(...params) || {};
				if (requestPayload.agent_input_context) {
					// Add additional payload from description completer to agent_input_context
					const agent_input_context = {
						...requestPayload?.agent_input_context,
						...additionalPayload,
					};
					return { ...requestPayload, agent_input_context };
				}
				return requestPayload;
			},
		};
		return modifiedConfigItem;
	}
	if (
		configItem?.getRequestPayload &&
		additionalPayload?.feature === 'issue-reformatter' &&
		fg('jira-work-sync-desc-comment-summary-ga')
	) {
		return {
			...configItem,
			getRequestPayload: (...params) => {
				const requestPayload = configItem.getRequestPayload?.(...params) || {};
				const aiFeatureInput = requestPayload?.ai_feature_input as
					| Record<string, unknown>
					| undefined;
				const existingContext = aiFeatureInput?.context as Record<string, unknown> | undefined;
				const payload = {
					ai_feature_input: {
						...aiFeatureInput,
						context: {
							...existingContext,
							description: additionalPayload?.additionalInfo,
						},
					},
				};
				return payload;
			},
		};
	}
	return configItem;
};

export const getDescriptionCompleterConfigItem = (
	configItem?: EditorPluginAIConfigItem,
	additionalPayload?: DescriptionCompleterAdditionalPayload,
): EditorPluginAIConfigItem | undefined => {
	if (configItem?.getRequestPayload) {
		const { source } = additionalPayload || {};
		// Use different experienceId for Google Docs
		const experienceId =
			source === 'GOOGLE_DOC_LINK' ? 'ai-issue-create-google-doc-single' : 'jira-work-completer';

		const modifiedConfigItem: EditorPluginAIConfigItem = {
			...configItem,
			endpoint:
				'/gateway/api/assist/ai/v2/ai-feature/jira/issue/source-type/link/suggestions/stream',
			experienceId,
			getRequestPayload: (params) => {
				const { context: contextFromPayload, source } = additionalPayload || {};

				const { ai_feature_input: input } = configItem.getRequestPayload?.(params) ?? {};
				const { context, ...additionalProps } = (input as Record<string, unknown>) ?? {};
				const { summary, description } = context as { description: string; summary: string };

				// Construct input for AI Work Create API
				const ai_feature_input = {
					...additionalProps,
					source,
					context: {
						...contextFromPayload,
						additional_context: {
							issue_summary: summary,
							issue_description: description,
						},
					},
				};
				return { ai_feature_input };
			},
		};

		return modifiedConfigItem;
	}
};

export const getDraftReplyPillConfigItem = (
	configItem?: EditorPluginAIConfigItem,
	additionalPayload?: AdditionalPayloadDataForDraftReplyPill,
): EditorPluginAIConfigItem | undefined => {
	if (configItem?.getRequestPayload) {
		const modifiedConfigItem: EditorPluginAIConfigItem = {
			...configItem,
			draftReplyMetadata: additionalPayload,
			featureId: 'brainstorm',
			endpoint: '/gateway/api/firebolt/1/agent-response/generate',
			getRequestPayload: (params) => {
				const payload = additionalPayload || {};
				const { userText } = configItem.getRequestPayload?.(params) ?? {
					userText: params?.selection || params?.doc,
				};

				return {
					...payload,
					...(additionalPayload?.category === 'ENHANCE_REPLY' ? { userText } : {}),
				};
			},
		};
		return modifiedConfigItem;
	}
};

export const getIncidentSmartCommentsConfigItem = (
	configItem?: EditorPluginAIConfigItem,
	additionalPayload?: IncidentSmartCommentsAdditionalPayload,
): EditorPluginAIConfigItem | undefined => {
	if (configItem?.getRequestPayload) {
		const modifiedConfigItem: EditorPluginAIConfigItem = {
			...configItem,
			getRequestPayload: (params) => {
				const originalPayload = configItem.getRequestPayload?.(params) || {};

				// Override intent with the runtime value from the pill
				return {
					...originalPayload,
					intent: additionalPayload?.replyIntent || 'REQUEST_MORE_INFO',
					isExternal: additionalPayload?.isExternal,
				};
			},
		};
		return modifiedConfigItem;
	}
	return configItem;
};
